import { useEffect, useMemo, useRef, useState } from 'react';
import { PROMPT_REPEAT_MIN_SECONDS, REWARD_ICONS, useSettings, type RewardIcon } from '../../settings/SettingsContext';
import { api } from '../../api/client';
import { canSpeak, speak, stopSpeaking } from '../speak';
import { spokenText } from '../phrases';
import { playCorrectPing } from '../ping';
import { endSession, fetchRewardSummary, type RewardSummary } from '../../rewards/rewards';
import { useEntitlements } from '../../subscription/offers';
import { isTalkBoardOpen, onTalkBoardChange, openTalkBoard, pausedMsSince, useGameBarTalk } from '../../talk/talkOpen';
import { customFor, onMediaChange } from '../../media/media';
import { translate, useT } from '../../i18n/i18n';
import { motionAllowed } from '../../designs/scene/programs';
import { sandboxPort, serverPort } from '../kit/shell';
import { TOOLKIT_VERSION, type Json, type Toolkit } from '../sdk/toolkit';
import { resolveModule, type Resolved } from './registry';

function storeKey(kind: string, learnerId: string | undefined) {
  return `game-store:${kind}:${learnerId ?? 'none'}`;
}

function readStore(key: string): Record<string, Json> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function GameHost({ kind, content: given, programId, learnerId }: {
  kind: string;
  content: Record<string, unknown>;
  programId?: string;
  learnerId?: string;
}) {
  const t = useT();
  const contentKey = JSON.stringify(given);
  const content = useMemo(() => given, [contentKey]);
  const { settings } = useSettings();
  const talk = !!useEntitlements()?.talkBoard;
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [status, setStatus] = useState<'playing' | 'empty' | null>(null);
  const [onBreak, setOnBreak] = useState(false);
  const [breakOffer, setBreakOffer] = useState<boolean | null>(null);
  const [collected, setCollected] = useState<RewardSummary | null>(null);
  const [earnedNow, setEarnedNow] = useState(0);
  const box = useRef<HTMLDivElement | null>(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const trialRef = useRef<string | null>(null);
  const resumers = useRef(new Set<() => void>());
  const answered = useRef(false);
  const live = !!programId;

  const port = useMemo(() => (programId ? serverPort(programId, learnerId) : sandboxPort()), [programId, learnerId]);

  const kit = useMemo<Toolkit>(() => {
    const key = storeKey(kind, learnerId);
    return {
      version: TOOLKIT_VERSION,
      kind,
      live,
      content,
      prefs: {
        reduceMotion: !motionAllowed(settings.reduceMotion),
        transitionType: settings.roundTransitionType,
        transitionMs: settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs,
        promptRepeatSeconds: Math.max(PROMPT_REPEAT_MIN_SECONDS, settings.promptRepeatSeconds),
        promptRepeatMinSeconds: PROMPT_REPEAT_MIN_SECONDS,
        language: document.documentElement.lang || 'en',
      },
      speech: {
        say(text) {
          const words = spokenText(text);
          if (!words || !canSpeak()) return;
          const s = settingsRef.current;
          void speak(words, { voiceName: s.ttsVoice, rate: s.ttsRate });
        },
        stop: stopSpeaking,
      },
      sound: { correct() { if (settingsRef.current.correctPingEnabled) playCorrectPing(); } },
      trials: {
        async register(presented, answer) {
          try {
            return await port.register(presented, answer);
          } catch (e) {
            setError(e instanceof Error ? e.message : translate('Could not start the round'));
            return null;
          }
        },
        async respond(trialId, response) {
          try {
            const v = await port.respond(trialId, response);
            answered.current = true;
            if (v.rewarded > 0) setEarnedNow((n) => n + v.rewarded);
            if (v.correct && settingsRef.current.correctPingEnabled) playCorrectPing();
            return v;
          } catch (e) {
            setError(e instanceof Error ? e.message : translate('Could not record that answer'));
            return null;
          }
        },
      },
      rounds: {
        available: live,
        async start() {
          if (!programId) return null;
          try {
            return await api<Record<string, unknown>>('/games/start', { method: 'POST', token: true, body: { programId, learnerId } });
          } catch (e) {
            setError(e instanceof Error ? e.message : translate('Could not start the round'));
            return null;
          }
        },
        async attempt(roundId, body) {
          try {
            const v = await api<Record<string, unknown>>(`/games/rounds/${roundId}/attempt`, { method: 'POST', token: true, body });
            if (v.correct === true || v.roundComplete === true) answered.current = true;
            const rewarded = typeof v.rewarded === 'number' ? v.rewarded : 0;
            if (rewarded > 0) setEarnedNow((n) => n + rewarded);
            if (v.correct === true && settingsRef.current.correctPingEnabled) playCorrectPing();
            return v;
          } catch (e) {
            setError(e instanceof Error ? e.message : translate('Could not record that answer'));
            return null;
          }
        },
      },
      session: {
        status: setStatus,
        offerBreak: setBreakOffer,
        current(trialId) { trialRef.current = trialId; },
        takeBreak() {
          stopSpeaking();
          const trial = trialRef.current;
          if (trial) void port.rest(trial);
          setOnBreak(true);
        },
        onResume(fn) {
          resumers.current.add(fn);
          return () => { resumers.current.delete(fn); };
        },
        fail: setError,
      },
      clock: {
        now: () => performance.now(),
        elapsedSince: (start) => performance.now() - start - pausedMsSince(start),
        paused: isTalkBoardOpen,
        onPauseChange: onTalkBoardChange,
      },
      media: {
        picture: (text) => customFor(text)?.image,
        onChange: onMediaChange,
      },
      store: {
        get: (k) => readStore(key)[k],
        set(k, value) {
          try {
            localStorage.setItem(key, JSON.stringify({ ...readStore(key), [k]: value }));
          } catch {
            return;
          }
        },
      },
      t: translate,
    };
  }, [kind, live, content, port, programId, learnerId, settings.reduceMotion, settings.roundTransitionType, settings.roundTransitionMs, settings.promptRepeatSeconds]);

  useEffect(() => {
    let cancelled = false;
    setResolved(null);
    void resolveModule(kind, content).then((r) => { if (!cancelled) setResolved(r); });
    return () => { cancelled = true; };
  }, [kind, content]);

  const module = resolved && 'module' in resolved ? resolved.module : null;

  useEffect(() => {
    const el = box.current;
    if (!el || !module) return;
    const mounted = module.mount(el, kit);
    return () => mounted.unmount();
  }, [module, kit]);

  useEffect(() => {
    if (!live || !settings.features.rewardTotal) return;
    let cancelled = false;
    void fetchRewardSummary(learnerId ?? null).then((s) => { if (!cancelled) setCollected(s); });
    return () => { cancelled = true; };
  }, [learnerId, settings.features.rewardTotal, live]);

  useEffect(() => {
    const session = crypto.randomUUID();
    return () => {
      stopSpeaking();
      if (live && programId) void endSession(programId, session, answered.current);
    };
  }, [programId, live]);

  const playing = !error && !onBreak && status === 'playing';
  useGameBarTalk(talk && playing);

  function resume() {
    setOnBreak(false);
    resumers.current.forEach((fn) => fn());
  }

  return (
    <>
      {!resolved && <p className="muted" role="status">{t('Loading…')}</p>}
      {resolved && 'missing' in resolved && (
        <div className="card"><p>{t('This program has nothing to practise yet.')}</p><p className="field-hint">{t(resolved.missing)}</p></div>
      )}
      {error && <div className="card"><div className="error" role="alert">{error}</div></div>}
      {!error && onBreak && (
        <div className="card">
          <h2>{t('Break time')}</h2>
          <p className="field-hint">{t('Asking to stop is written down as asking to stop. It is not a wrong answer.')}</p>
          <div className="toolbar">
            <button className="btn" onClick={resume}>{t('Ready to carry on')}</button>
          </div>
        </div>
      )}
      {playing && live && settings.features.rewardTotal && collected?.enabled && (
        <div className="board-total">
          <span aria-hidden="true">{REWARD_ICONS[collected.icon as RewardIcon] ?? REWARD_ICONS.star}</span>
          <strong>{collected.total + earnedNow}</strong>
          <span>{collected.name}</span>
        </div>
      )}
      <div ref={box} className="game-host" hidden={!!error || onBreak} />
      {playing && ((breakOffer ?? settings.features.assentBreak) || talk) && (
        <div className="toolbar break-bar">
          {(breakOffer ?? settings.features.assentBreak) && (
            <button className="btn secondary" onClick={kit.session.takeBreak}>{t('I need a break')}</button>
          )}
          {talk && (
            <button className="btn secondary" aria-haspopup="dialog" onClick={openTalkBoard}>
              <span aria-hidden="true">💬</span> {t('Talk')}
            </button>
          )}
        </div>
      )}
    </>
  );
}
